import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import type { Lang } from '../../i18n'
import { EggArt } from './EggArt'
import { eggPacks } from './config'
import './eggs.css'

/**
 * Floating, draggable language collectibles plus the rocket-launch celebration.
 * Objects drift across the screen, can be grabbed and thrown, and reveal a secret on click.
 * Finding all three unlocks a badge; progress is remembered per language in this browser.
 */

export const LAUNCH_EVENT = 'abv-launch'
export const announceLaunch = () => window.dispatchEvent(new Event(LAUNCH_EVENT))

type Body = { x: number; y: number; vx: number; vy: number; angle: number; spin: number; dragging: boolean; moved: number }

const SIZE = 64
const MARGIN = 10
const FLASH_MS = 5000
// Large system UI the finds steer around; planet labels are small and simply stack above them.
const SYSTEM_UI = '.u-panel, .u-bar, .u-log, .u-coach, .u-flying, .u-logdone'
const readSet = (key: string) => {
  try { return new Set<string>(JSON.parse(window.localStorage.getItem(key) || '[]')) } catch { return new Set<string>() }
}
const writeSet = (key: string, value: Set<string>) => {
  try { window.localStorage.setItem(key, JSON.stringify([...value])) } catch { /* storage unavailable */ }
}

const overlaps = (x: number, y: number, rect: DOMRect) =>
  x > rect.left - MARGIN - SIZE && x < rect.right + MARGIN && y > rect.top - MARGIN - SIZE && y < rect.bottom + MARGIN

/**
 * Moves a find out from under a panel by the shortest way that stays on screen, at most `limit` px per call.
 * Exits that would land under another panel are used only when nothing else is left, so a find never gets stuck in a corner between two.
 */
function steerClear(body: Body, rects: DOMRect[], limit: number, width: number, height: number, top: number) {
  const rect = rects.find((item) => overlaps(body.x, body.y, item))
  if (!rect) return
  const exits = [
    { x: rect.left - MARGIN - SIZE, y: body.y },
    { x: rect.right + MARGIN, y: body.y },
    { x: body.x, y: rect.top - MARGIN - SIZE },
    { x: body.x, y: rect.bottom + MARGIN },
  ].filter((point) => point.x >= 8 && point.x <= width - SIZE - 8 && point.y >= top && point.y <= height - SIZE - 8)
  const free = exits.filter((point) => !rects.some((other) => overlaps(point.x, point.y, other)))
  const pool = free.length ? free : exits
  if (!pool.length) return
  const away = (point: { x: number; y: number }) => Math.hypot(point.x - body.x, point.y - body.y)
  const exit = pool.reduce((best, point) => (away(point) < away(best) ? point : best))
  const dx = exit.x - body.x
  const dy = exit.y - body.y
  const distance = Math.hypot(dx, dy)
  const step = Math.min(distance, limit)
  body.x += (dx / distance) * step
  body.y += (dy / distance) * step
  if (dx) body.vx = Math.abs(body.vx) * Math.sign(dx)
  if (dy) body.vy = Math.abs(body.vy) * Math.sign(dy)
}

type FieldProps = { lang: Lang; active: boolean; inSystem: boolean; onComplete: () => void }

export function EggField({ lang, active, inSystem, onComplete }: FieldProps) {
  const pack = eggPacks[lang]
  const storageKey = `abv-eggs-${lang}`
  const [found, setFound] = useState<Set<string>>(() => readSet(storageKey))
  const [hidden, setHidden] = useState(() => { try { return window.localStorage.getItem('abv-eggs-hidden') === '1' } catch { return false } })
  const [bubble, setBubble] = useState<{ index: number; x: number; y: number } | null>(null)
  const [burst, setBurst] = useState<{ x: number; y: number; key: number } | null>(null)
  const [celebrate, setCelebrate] = useState(false)
  const [flash, setFlash] = useState(0)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const hostRef = useRef<HTMLElement | null>(null)
  const nodes = useRef<Array<HTMLButtonElement | null>>([])
  const bodies = useRef<Body[]>([])
  const pointer = useRef<{ index: number; lastX: number; lastY: number; lastT: number } | null>(null)
  const reduced = useRef(window.matchMedia('(prefers-reduced-motion: reduce)').matches)

  useEffect(() => { setFound(readSet(storageKey)); setBubble(null) }, [storageKey])

  // Inside the system the finds move into its own layer, under labels and panels, so they never cover a control.
  useEffect(() => {
    if (!inSystem) {
      setHost(null)
      return
    }
    const find = () => {
      const element = document.querySelector<HTMLElement>('.app > .universe')
      setHost((current) => (current === element ? current : element))
    }
    find()
    // The system is lazy-loaded and remounts on a language switch: follow its root as it comes and goes.
    const observer = new MutationObserver(find)
    const app = document.querySelector('.app')
    if (app) observer.observe(app, { childList: true })
    return () => observer.disconnect()
  }, [inSystem])
  useEffect(() => { hostRef.current = host }, [host])

  useEffect(() => {
    const width = window.innerWidth
    const height = window.innerHeight
    bodies.current = pack.items.map((_, index) => ({
      x: width * (0.18 + index * 0.3) + Math.random() * 60,
      y: height * (0.3 + ((index * 0.27) % 0.5)),
      vx: (Math.random() > 0.5 ? 1 : -1) * (14 + Math.random() * 14),
      vy: (Math.random() > 0.5 ? 1 : -1) * (8 + Math.random() * 10),
      angle: Math.random() * 360,
      spin: (Math.random() - 0.5) * 20,
      dragging: false,
      moved: 0,
    }))
    let frame = 0
    let last = performance.now()
    let tick = 0
    let rects: DOMRect[] = []
    const step = (now: number) => {
      frame = requestAnimationFrame(step)
      const delta = Math.min((now - last) / 1000, 0.05)
      last = now
      const system = hostRef.current
      if (tick++ % 20 === 0) {
        rects = system ? [...system.querySelectorAll(SYSTEM_UI)].map((element) => element.getBoundingClientRect()).filter((rect) => rect.width > 0 && rect.height > 0) : []
      }
      const w = window.innerWidth
      const h = window.innerHeight
      const top = system ? 8 : 90
      bodies.current.forEach((body, index) => {
        if (!body.dragging) {
          if (!reduced.current) {
            // Thrown objects slow down towards a calm drift.
            const speed = Math.hypot(body.vx, body.vy)
            if (speed > 30) { body.vx *= 1 - delta * 1.6; body.vy *= 1 - delta * 1.6 }
            body.x += body.vx * delta
            body.y += body.vy * delta
            body.angle += body.spin * delta
          }
          if (body.x < 8 || body.x > w - SIZE - 8) { body.vx *= -1; body.x = Math.min(Math.max(body.x, 8), w - SIZE - 8) }
          if (body.y < top || body.y > h - SIZE - 8) { body.vy *= -1; body.y = Math.min(Math.max(body.y, top), h - SIZE - 8) }
          // Glide out from under panels and bars (instantly with reduced motion).
          if (rects.length) steerClear(body, rects, reduced.current ? Infinity : 900 * delta, w, h, top)
        }
        const node = nodes.current[index]
        if (node) node.style.transform = `translate3d(${body.x.toFixed(1)}px, ${body.y.toFixed(1)}px, 0) rotate(${body.angle.toFixed(1)}deg)`
      })
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [pack])

  useEffect(() => {
    const onLaunch = () => {
      const width = window.innerWidth
      setBurst({ x: width / 2, y: window.innerHeight * 0.62, key: Date.now() })
    }
    window.addEventListener(LAUNCH_EVENT, onLaunch)
    return () => window.removeEventListener(LAUNCH_EVENT, onLaunch)
  }, [])

  useEffect(() => {
    if (!bubble) return
    const timer = window.setTimeout(() => setBubble(null), 6500)
    return () => window.clearTimeout(timer)
  }, [bubble])

  useEffect(() => {
    if (!flash) return
    const timer = window.setTimeout(() => setFlash(0), FLASH_MS)
    return () => window.clearTimeout(timer)
  }, [flash])

  const onDown = (event: ReactPointerEvent<HTMLButtonElement>, index: number) => {
    const body = bodies.current[index]
    body.dragging = true
    body.moved = 0
    pointer.current = { index, lastX: event.clientX, lastY: event.clientY, lastT: performance.now() }
    event.currentTarget.setPointerCapture(event.pointerId)
  }
  const onMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const state = pointer.current
    if (!state) return
    const body = bodies.current[state.index]
    const now = performance.now()
    const dx = event.clientX - state.lastX
    const dy = event.clientY - state.lastY
    const dt = Math.max((now - state.lastT) / 1000, 0.001)
    body.x += dx
    body.y += dy
    body.moved += Math.abs(dx) + Math.abs(dy)
    body.vx = THREE_CLAMP(dx / dt)
    body.vy = THREE_CLAMP(dy / dt)
    state.lastX = event.clientX
    state.lastY = event.clientY
    state.lastT = now
  }
  const onUp = (index: number) => {
    const body = bodies.current[index]
    body.dragging = false
    pointer.current = null
    if (body.moved < 6) reveal(index)
    else body.spin = body.vx * 0.2
  }

  const reveal = (index: number) => {
    const body = bodies.current[index]
    setBubble({ index, x: body.x + SIZE / 2, y: body.y })
    setBurst({ x: body.x + SIZE / 2, y: body.y + SIZE / 2, key: Date.now() })
    const item = pack.items[index]
    if (!found.has(item.id)) setFlash(Date.now())
    setFound((current) => {
      if (current.has(item.id)) return current
      const next = new Set(current).add(item.id)
      writeSet(storageKey, next)
      if (next.size === pack.items.length) window.setTimeout(() => setCelebrate(true), 900)
      return next
    })
  }

  const toggleHidden = () => {
    setHidden((value) => {
      try { window.localStorage.setItem('abv-eggs-hidden', value ? '0' : '1') } catch { /* storage unavailable */ }
      return !value
    })
  }

  // Until the system's layer is found the finds stay hidden rather than float above its controls.
  const waiting = inSystem && !host
  const field = (
    <div className={`egg-field${active ? '' : ' dim'}${host ? ' in-system' : ''}${waiting ? ' waiting' : ''}`} inert={!active || waiting || undefined}>
      {!hidden && pack.items.map((item, index) => (
        <button
          key={item.id}
          ref={(element) => { nodes.current[index] = element }}
          className={`egg-object${found.has(item.id) ? ' found' : ''}`}
          aria-label={item.label}
          title={item.label}
          onPointerDown={(event) => onDown(event, index)}
          onPointerMove={onMove}
          onPointerUp={() => onUp(index)}
          onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); reveal(index) } }}
        >
          <EggArt id={item.id} />
        </button>
      ))}

      <div className={`egg-tray${found.size ? '' : ' empty'}${flash ? ' flash' : ''}`}>
        <span className="egg-count" title={pack.hint}>
          {pack.items.map((item) => <i key={item.id} className={found.has(item.id) ? 'on' : ''}><EggArt id={item.id} /></i>)}
          <em>{pack.found} {found.size}/{pack.items.length}</em>
        </span>
        <button onClick={toggleHidden}>{hidden ? pack.show : pack.hide}</button>
      </div>
    </div>
  )

  return (
    <>
      {host ? createPortal(field, host) : field}
      <div className={`egg-overlay${active ? '' : ' dim'}`} aria-live="polite">
        {bubble && !hidden && (
          <div className="egg-bubble" style={{ left: Math.min(Math.max(bubble.x, 170), window.innerWidth - 170), top: Math.max(bubble.y - 12, 100) } as CSSProperties}>
            <b>{pack.items[bubble.index].label}</b>
            <p>{pack.items[bubble.index].secret}</p>
          </div>
        )}

        {burst && <Confetti key={burst.key} x={burst.x} y={burst.y} pieces={pack.confetti} />}

        {celebrate && (
          <div className="egg-complete" role="dialog" aria-label={pack.complete.title}>
            <div className="egg-complete-art">{pack.items.map((item) => <EggArt key={item.id} id={item.id} />)}</div>
            <b>{pack.complete.title}</b>
            <p>{pack.complete.text}</p>
            <div>
              <button className="u-btn" onClick={() => { setCelebrate(false); onComplete() }}>🚀 {pack.complete.cta}</button>
              <button className="u-btn ghost" onClick={() => setCelebrate(false)}>✕</button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function THREE_CLAMP(value: number) {
  return Math.max(-900, Math.min(900, value))
}

export function Confetti({ x, y, pieces }: { x: number; y: number; pieces: string[] }) {
  const [items] = useState(() => Array.from({ length: 16 }, (_, index) => ({
    id: pieces[index % pieces.length],
    dx: (Math.random() - 0.5) * 520,
    dy: -140 - Math.random() * 320,
    rot: (Math.random() - 0.5) * 720,
    delay: Math.random() * 0.15,
    size: 26 + Math.random() * 22,
  })))
  const [alive, setAlive] = useState(true)
  useEffect(() => { const timer = window.setTimeout(() => setAlive(false), 2400); return () => window.clearTimeout(timer) }, [])
  if (!alive) return null
  return (
    <div className="egg-confetti" style={{ left: x, top: y }} aria-hidden="true">
      {items.map((item, index) => (
        <i key={index} style={{ '--dx': `${item.dx}px`, '--dy': `${item.dy}px`, '--rot': `${item.rot}deg`, '--delay': `${item.delay}s`, width: item.size, height: item.size } as CSSProperties}>
          <EggArt id={item.id as never} />
        </i>
      ))}
    </div>
  )
}

export function LaunchCall({ text }: { text: string }) {
  const [shown, setShown] = useState(0)
  useEffect(() => {
    const onLaunch = () => setShown(Date.now())
    window.addEventListener(LAUNCH_EVENT, onLaunch)
    return () => window.removeEventListener(LAUNCH_EVENT, onLaunch)
  }, [])
  useEffect(() => {
    if (!shown) return
    const timer = window.setTimeout(() => setShown(0), 1900)
    return () => window.clearTimeout(timer)
  }, [shown])
  if (!shown) return null
  return <div className="launch-call" key={shown}>{text}</div>
}
